import React, { useEffect, useMemo, useState } from "react";
import { format, isValid, parse } from "date-fns";

import AppButton from "../../design-system/components/AppButton";
import AppCallout from "../../design-system/components/AppCallout";
import AppDialog from "../../design-system/components/AppDialog";
import AppDialogBody from "../../design-system/components/AppDialogBody";
import AppDialogFooter from "../../design-system/components/AppDialogFooter";
import AppDialogHeader from "../../design-system/components/AppDialogHeader";
import AppField from "../../design-system/components/AppField";
import AppFieldStack from "../../design-system/components/AppFieldStack";
import AppRadioGroup from "../../design-system/components/AppRadioGroup";
import AppSelect from "../../design-system/components/AppSelect";
import AppText from "../../design-system/components/AppText";
import { IconWarning } from "../../design-system/icons";

const formatDate = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";
  const parsedDate = parse(rawValue.slice(0, 10), "yyyy-MM-dd", new Date());
  return isValid(parsedDate) ? format(parsedDate, "dd.MM.yyyy") : rawValue;
};

/**
 * Entscheidung ueber die Schichten, die nach einem Austrittsdatum offen bleiben.
 *
 * Wird nur geoeffnet, wenn es solche Schichten ueberhaupt gibt -- der Aufrufer
 * fragt das vorher ueber /users/:id/exit-impact ab.
 */
export default function ExitShiftsDialog({
  open,
  userName = "",
  exitDate = "",
  impact = null,
  saving = false,
  onCancel,
  onConfirm,
}) {
  const [mode, setMode] = useState("transfer");
  const [targetUserId, setTargetUserId] = useState("");

  const candidates = useMemo(
    () => impact?.transfer_candidates || [],
    [impact]
  );

  // Ohne Uebernahme-Kandidaten bleibt nur das Loeschen -- dann startet der
  // Dialog auch dort, statt auf einer nicht waehlbaren Option zu stehen.
  useEffect(() => {
    if (!open) return;
    setMode(candidates.length ? "transfer" : "delete");
    setTargetUserId("");
  }, [candidates.length, open]);

  if (!open || !impact) {
    return null;
  }

  const shiftCount = impact.shift_count || 0;
  const locationNames = impact.location_names || [];
  const periodStart = formatDate(impact.first_shift_date);
  const periodEnd = formatDate(impact.last_shift_date);
  const period =
    periodStart && periodEnd
      ? periodStart === periodEnd
        ? periodStart
        : `${periodStart} – ${periodEnd}`
      : "";

  const needsTarget = mode === "transfer";
  const confirmDisabled = saving || (needsTarget && !targetUserId);

  const options = [
    {
      value: "transfer",
      label: "Schichten übertragen",
      description: candidates.length
        ? "Alle offenen Schichten werden einer anderen Person zugewiesen."
        : "Am betroffenen Standort ist niemand verfügbar, der die Schichten übernehmen kann.",
      disabled: !candidates.length,
    },
    {
      value: "delete",
      label: "Schichten löschen",
      description: "Die Einträge werden aus dem Schichtplan entfernt. Der Bedarf bleibt offen.",
    },
  ];

  const candidateOptions = candidates.map((candidate) => ({
    value: String(candidate.id),
    label: candidate.name,
  }));

  return (
    <AppDialog open={open} size="md" onClose={saving ? undefined : onCancel}>
      <AppDialogHeader
        title="Schichten nach dem Austritt"
        subtitle={
          userName && exitDate
            ? `${userName} · letzter Arbeitstag ${formatDate(exitDate) || exitDate}`
            : undefined
        }
        visual={<IconWarning sx={{ fontSize: 18 }} />}
      />
      <AppDialogBody>
        <AppFieldStack>
          <AppCallout
            variant="warning"
            description={
              `Für diese Person ${shiftCount === 1 ? "ist" : "sind"} nach dem Austrittsdatum ` +
              `noch ${shiftCount} ${shiftCount === 1 ? "Schicht" : "Schichten"} geplant` +
              (period ? ` (${period})` : "") +
              (locationNames.length ? ` · ${locationNames.join(", ")}` : "") +
              "."
            }
          />
          <AppText variant="body-2">
            Was soll mit diesen Schichten passieren?
          </AppText>
          <AppRadioGroup
            options={options}
            value={mode}
            onChange={(event) => setMode(event.target.value)}
            aria-label="Umgang mit den offenen Schichten"
          />
          {needsTarget ? (
            <AppField label="Schichten übernehmen" required>
              <AppSelect
                value={targetUserId}
                onChange={(event) => setTargetUserId(event.target.value)}
                options={candidateOptions}
                placeholder="Person auswählen"
                searchable
                searchPlaceholder="Mitarbeitende suchen"
              />
            </AppField>
          ) : null}
        </AppFieldStack>
      </AppDialogBody>
      <AppDialogFooter>
        <AppButton variant="neutral" onClick={onCancel} disabled={saving}>
          Abbrechen
        </AppButton>
        <AppButton
          variant={mode === "delete" ? "error" : "primary"}
          disabled={confirmDisabled}
          onClick={() =>
            onConfirm(
              mode === "transfer"
                ? { mode: "transfer", target_user_id: Number(targetUserId) }
                : { mode: "delete" }
            )
          }
        >
          {mode === "delete" ? "Schichten löschen" : "Schichten übertragen"}
        </AppButton>
      </AppDialogFooter>
    </AppDialog>
  );
}
